import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import SingleLineEllipsis from '@deltek/specification-client/core/Feedbacks/SingleLineEllipsis'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import Paper from '@material-ui/core/Paper'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight'

import clsx from 'clsx'
import { onEnterKeyDown } from 'pages/ProjectsPage/WorkspacePage/components/Common/utils'
import React from 'react'
import ReactResizeDetector from 'react-resize-detector'
import { Row } from 'react-table'

interface ElementNameBlockStyleProps {
    isProduct?: boolean
    canExpand?: boolean
    textWidth?: number
    isSelectedForPreview?: boolean
    isBPImport?: boolean
    isLoadingActive?: boolean
}
const useElementNameBlockStyles = makeStyles((theme: Theme) => ({
    elementNameBlockPaper: {
        padding: theme.spacing(1, 2),
        color: (props: ElementNameBlockStyleProps) =>
            props?.isBPImport && !props?.isProduct
                ? theme.palette.common.white
                : theme.palette.common.black,
        backgroundColor: (props: ElementNameBlockStyleProps) =>
            props?.isBPImport && !props?.isProduct
                ? '#677B98'
                : theme.palette.common.white,
        height: 60,
        paddingLeft: 0,
        paddingRight: (props: ElementNameBlockStyleProps) =>
            props.isLoadingActive ? theme.spacing(0.5) : theme.spacing(2),
        marginLeft: (props: ElementNameBlockStyleProps) =>
            !props.isLoadingActive && props?.isBPImport
                ? props.isProduct
                    ? theme.spacing(6)
                    : theme.spacing(2.5)
                : props.isProduct
                ? theme.spacing(3)
                : 0,
        display: 'flex',
        alignItems: 'center',
        marginRight: (props: ElementNameBlockStyleProps) =>
            props.isBPImport ? theme.spacing(2) : 0,

        borderRadius: (props: ElementNameBlockStyleProps) =>
            props?.isBPImport ? 3 : 4,
        position: (props: ElementNameBlockStyleProps) =>
            props?.isBPImport ? 'relative' : 'unset',
    },
    importedItemStrip: {
        position: 'absolute',
        borderRight: '5px solid #EC903B',
        height: '100%',
        right: 0,
        borderBottomRightRadius: 3,
        borderTopRightRadius: 3,
    },
    nameText: {
        ...theme.typography.body1,
        fontSize: pxToRem(14),
    },
    levelText: {
        ...theme.typography.caption,
        textAlign: 'start',
        paddingLeft: (props: ElementNameBlockStyleProps) =>
            props.isProduct
                ? theme.spacing(1)
                : !props.canExpand
                ? theme.spacing(2)
                : theme.spacing(4),
    },
    nameLink: {
        textDecoration: 'none',
        paddingLeft: (props: ElementNameBlockStyleProps) =>
            props.isProduct
                ? theme.spacing(1)
                : props.canExpand
                ? theme.spacing(0.5)
                : theme.spacing(2),
    },
    keyBoardArrowIcon: {
        display: 'block',
        cursor: 'pointer',
        color: '#0000008a ',
        zIndex: 111,
    },
    hidden: {
        display: 'none',
    },
    barContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
    },
    columnName: {
        display: 'flex',
        alignItems: 'center',
    },
}))

interface ElementNameBlockProps extends ElementNameBlockStyleProps {
    row: Row

    level?: string
    isSelectedForPreview?: boolean
    getTextWidth?: (width: number) => void
    handleExpandRow?: (
        rowId: string,
        value: boolean,
        event:
            | React.MouseEvent<SVGSVGElement>
            | React.KeyboardEvent<SVGSVGElement>
    ) => void
    handleSelectFileForPreview?: (
        fileKey?: string,
        selectedProduct?: string
    ) => void
    fileKey?: string
    isLoadingActive?: boolean
    isFileImported?: boolean
    lastFocusedExpandCollapseIconRef?: React.MutableRefObject<string | null>
}

const ElementNameBlock: React.FC<ElementNameBlockProps> = ({
    row,
    level,
    canExpand,
    textWidth,
    getTextWidth,
    handleExpandRow,
    handleSelectFileForPreview,
    isSelectedForPreview,
    fileKey,
    isBPImport,
    isLoadingActive,
    isFileImported,
    lastFocusedExpandCollapseIconRef,
}) => {
    const classes = useElementNameBlockStyles({
        isProduct: row?.id?.includes('.'),
        canExpand: canExpand,
        textWidth:
            isFileImported && !row?.id?.includes('.')
                ? textWidth! - 10
                : textWidth,
        isSelectedForPreview: isSelectedForPreview,
        isBPImport: isBPImport,
        isLoadingActive: isLoadingActive,
    })
    const handleTextWidth = (width: number) => {
        getTextWidth?.(width)
    }
    const expandCollapseIconRef = React.useRef<SVGSVGElement>(null)
    const showImportedItem = React.useMemo(() => {
        return isBPImport && !row?.id?.includes('.') && isFileImported
    }, [isBPImport, row, isFileImported])
    const handleClickName = React.useCallback(() => {
        if (level) {
            handleSelectFileForPreview?.(
                fileKey,
                level === 'Product Type' ? row?.values?.expander : undefined
            )
        }
    }, [handleSelectFileForPreview, row, level])

    React.useEffect(() => {
        // refocus on the expand collapse icon if it was the last focused element
        if (lastFocusedExpandCollapseIconRef?.current === row.id) {
            expandCollapseIconRef.current?.focus()
        }
    }, [lastFocusedExpandCollapseIconRef, row.id])
    return (
        <Paper
            variant="elevation"
            data-testid="elementNameImport"
            elevation={0}
            className={clsx(
                row?.values?.expander?.length !== 0 &&
                    classes.elementNameBlockPaper
            )}
            onClick={handleClickName}
            onKeyDown={onEnterKeyDown(handleClickName)}
            tabIndex={0}
        >
            <span data-testid="elementName">
                {' '}
                <span className={classes.barContainer}>
                    {' '}
                    {row?.subRows?.length > 0 && row.isExpanded ? (
                        <KeyboardArrowDownIcon
                            className={clsx(
                                'arrow-down',
                                classes.keyBoardArrowIcon
                            )}
                            ref={expandCollapseIconRef}
                            onClick={(event: React.MouseEvent<SVGSVGElement>) =>
                                handleExpandRow?.(row.id, true, event)
                            }
                            onKeyDown={onEnterKeyDown((e) => {
                                handleExpandRow?.(row.id, true, e)
                                if (lastFocusedExpandCollapseIconRef) {
                                    lastFocusedExpandCollapseIconRef.current =
                                        row.id
                                }
                            })}
                            tabIndex={0}
                        />
                    ) : (
                        row?.subRows?.length > 0 && (
                            <KeyboardArrowRightIcon
                                className={clsx(
                                    'arrow-right',
                                    classes.keyBoardArrowIcon
                                )}
                                ref={expandCollapseIconRef}
                                onClick={(
                                    event: React.MouseEvent<SVGSVGElement>
                                ) => handleExpandRow?.(row.id, false, event)}
                                onKeyDown={onEnterKeyDown((e) => {
                                    handleExpandRow?.(row.id, false, e)
                                    if (lastFocusedExpandCollapseIconRef) {
                                        lastFocusedExpandCollapseIconRef.current =
                                            row.id
                                    }
                                })}
                                tabIndex={0}
                            />
                        )
                    )}
                    <span className={classes.nameLink}>
                        <SingleLineEllipsis
                            text={row?.values?.expander}
                            className={classes.nameText}
                        >
                            {row?.values?.expander}
                        </SingleLineEllipsis>
                    </span>
                </span>
                <Typography className={classes.levelText}>{level}</Typography>
            </span>
            {showImportedItem && (
                <HoverTooltip
                    data-testid="importedFileStrip"
                    className={classes.importedItemStrip}
                    children={<div></div>}
                    placements={['right']}
                    text={`Imported on\n${row?.values?.dateTimeImported}`}
                />
            )}
            <ReactResizeDetector
                handleHeight={false}
                handleWidth={true}
                onResize={(width: number) => handleTextWidth(width)}
            />
        </Paper>
    )
}

export default ElementNameBlock
